import {
  AppointmentType,
  BusinessAppointmentType,
} from '../../../types/entities/appointments'

/**
 * Returns the normalized reason ID (type) from an appointment type value.
 * Accepts a string or AppointmentType object. Assumes normalization has already occurred.
 */
export function getTypeFromAppointmentType(
  type: string | AppointmentType | null | undefined,
  appointmentTypesMap: Record<string, BusinessAppointmentType>,
): string {
  if (!type) return ''
  if (typeof type === 'string') {
    if (appointmentTypesMap[type]) return type
    const found = Object.entries(appointmentTypesMap).find(
      ([, obj]: [string, BusinessAppointmentType]) => obj.eventTypeId === type,
    )
    return found ? found[0] : ''
  }
  if (typeof type === 'object' && type !== null && 'id' in type) {
    const found = Object.entries(appointmentTypesMap).find(
      ([, obj]: [string, BusinessAppointmentType]) =>
        obj.id === type.id || obj.eventTypeId === type.id,
    )
    return found ? found[0] : ''
  }
  return ''
}

/**
 * Normalizes the appointment type field to always be a string (ID).
 * Use this when loading appointment data from the backend to ensure FE state consistency.
 * Returns the same object type but with type as string.
 */
export function normalizeAppointmentTypeField<
  T extends { type?: string | AppointmentType | BusinessAppointmentType },
>(appointment: T): Omit<T, 'type'> & { type: string | undefined } {
  let normalizedType: string | undefined
  if (
    appointment.type &&
    typeof appointment.type === 'object' &&
    'id' in appointment.type
  ) {
    normalizedType = appointment.type.id
  } else if (typeof appointment.type === 'string') {
    normalizedType = appointment.type
  } else {
    normalizedType = undefined
  }
  return {
    ...appointment,
    type: normalizedType,
  }
}
